/**
 * Scroll reveal for pn-motion.css.
 *
 * The entrances are CSS animations, and they play on load by themselves. This
 * script's one job is to hold back the ones that start below the fold, so they
 * play as the reader reaches them rather than unseen on load, and to play each
 * row of cards one behind the other.
 *
 * Why it cannot lose content:
 * - Without this script every entrance plays once on load and ends visible.
 *   Anything that throws in here releases everything it was holding.
 * - It only holds an element that has a box and sits wholly below the window.
 *   Anything on screen at all is left to play, so nothing visible blinks out.
 * - A sweep on scroll, on resize and once after two seconds releases anything
 *   whose top has passed the bottom of the window: a jump to an anchor, a
 *   restored scroll position, a flick that crosses a section between frames.
 *   It listens in the capture phase, because the desktop layout scrolls an
 *   inner element and scroll events do not bubble.
 * - Printing releases everything first.
 *
 * On a slow phone this deferred script can run after the entrances below the
 * fold have already played unseen. The listener in the layout's head records
 * every entrance that starts, so those are found and replayed too, not only
 * the ones still running.
 */
(function () {
  var seen = window.pnMotionSeen || [];
  window.pnMotionSeen = null; // stop recording; the hero replays its slides for ever

  if (!('IntersectionObserver' in window) || typeof document.getAnimations !== 'function') return;
  if (window.Shopify && window.Shopify.designMode) return; // the editor redraws sections; let them play
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* Pieces that come in rows: cards that share a top edge arrive one behind
     the other. Keep this in step with the card and curtain rules in
     pn-motion.css. */
  var ROWED = [
    '.product-card',
    '.pn-post-card',
    '.pn-signal',
    '.pn-step',
    '.pn-pillar',
    '.pn-card',
    '.pn-contact__point',
    '.pn-contact__card',
    '.pn-ugc__slot',
    '.pn-faq__answers details',
    '.pn-footer__col',
    '.collection-card',
  ].join(',');

  /* Anything that moves as one piece: a nested entrance (a tile's photograph,
     its words, a card's image, the Hot Pick frames) waits for the piece it
     sits in, and a held piece holds every animation inside it. */
  var PIECE = [
    ROWED,
    '.pn-pick__stage',
    '.pn-pillars__media',
    '.pn-product-feature__media',
    '.pn-article__media',
    '.pn-learn__featured-media',
  ].join(',');

  var MAX_STEP = 3; // four in a row at most; beyond that the last reads as broken, not choreographed
  var HOLD = 'pn-hold';

  var held = new Set(); // pieces waiting to arrive

  function isEntrance(name) {
    return typeof name === 'string' && name.indexOf('pn-in-') === 0;
  }

  function release(piece) {
    if (!held.delete(piece)) return;
    piece.classList.remove(HOLD);
  }

  function releaseAll() {
    Array.from(held).forEach(release);
  }

  function setup() {
    var viewport = window.innerHeight || document.documentElement.clientHeight;

    // Every element with an entrance: the ones still running or waiting on a
    // delay, plus the ones the head listener saw start, which may be done.
    var targets = new Set(seen);
    document.getAnimations().forEach(function (anim) {
      if (!isEntrance(anim.animationName)) return;
      var target = anim.effect && anim.effect.target;
      if (target) targets.add(target);
    });

    targets.forEach(function (el) {
      if (!el.isConnected) return;
      var piece = el.closest(PIECE) || el;
      var rect = piece.getBoundingClientRect();
      if (!rect.width && !rect.height) return; // no box: closed, hidden or display none
      if (rect.top < viewport) return; // on screen now, or already passed
      held.add(piece);
    });

    if (!held.size) return;

    // A row is the cards that share a top edge, measured before anything is
    // held so the rows are the ones the reader will see. Each card waits one
    // step behind the card to its left, and the next row starts again at 0.
    var rows = new Map();
    held.forEach(function (piece) {
      if (!piece.matches(ROWED)) return;
      var rect = piece.getBoundingClientRect();
      var key = Math.round(rect.top);
      var row = rows.get(key) || [];
      row.push({ piece: piece, left: rect.left });
      rows.set(key, row);
    });
    rows.forEach(function (row) {
      row.sort(function (a, b) {
        return a.left - b.left;
      });
      row.forEach(function (item, n) {
        item.piece.style.setProperty('--pn-i', String(Math.min(n, MAX_STEP)));
      });
    });

    // Hold: the piece is hidden and every animation in it removed, which the
    // stylesheet does for .pn-hold. Releasing puts the animations back, which
    // starts them again from their first frame, delays and all.
    held.forEach(function (piece) {
      piece.classList.add(HOLD);
    });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          release(entry.target); // once, never on a later pass
        });
      },
      // A little short of the bottom edge, so the entrance finishes as it
      // arrives rather than starting the moment a sliver appears.
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );

    held.forEach(function (piece) {
      io.observe(piece);
    });

    function sweep() {
      var height = window.innerHeight || document.documentElement.clientHeight;
      Array.from(held).forEach(function (piece) {
        if (!piece.isConnected || piece.getBoundingClientRect().top < height) {
          io.unobserve(piece);
          release(piece);
        }
      });
    }

    var queued = false;
    function onScroll() {
      if (queued || !held.size) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        sweep();
      });
    }

    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('beforeprint', releaseAll);
    setTimeout(sweep, 2000);
  }

  function start() {
    try {
      setup();
    } catch (error) {
      releaseAll();
      throw error;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
